import React, { useEffect, useState } from "react";
import api, { apiError, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, DateField } from "@/components/fields";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Plus, FilePdf, EnvelopeSimple, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { tenant_id: "", invoice_id: "", lease_id: "", amount: 0, method: "mpesa", narration: "rent", reference: "", date: "" };
const NARRATIONS = [
  { value: "rent", label: "Rent" }, { value: "deposit", label: "Security Deposit" },
  { value: "direct_landlord", label: "Direct Landlord Payment" }, { value: "advance", label: "Advance" },
  { value: "other", label: "Other" },
];

export default function Payments() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [leases, setLeases] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const canCreate = ["property_manager", "property_admin", "accountant"].includes(user.role);

  const load = () => {
    api.get("/payments").then((r) => setItems(r.data));
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/leases").then((r) => setLeases(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.tenant_id || !form.amount) return toast.error("Tenant and amount are required");
    try {
      await api.post("/payments", { ...form, invoice_id: form.invoice_id || null, lease_id: form.lease_id || null });
      toast.success("Payment recorded & receipt generated"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const email = async (id) => {
    try { const r = await api.post(`/payments/${id}/email`);
      if (r.data.status === "success") toast.success("Receipt emailed"); else toast.error("Email failed: " + (r.data.detail || r.data.reason));
    } catch (e) { toast.error(apiError(e)); }
  };

  const notify = async (id) => {
    try { const r = await api.post(`/payments/${id}/notify`);
      if (!r.data.configured) toast.message("Twilio not configured — SMS/WhatsApp skipped");
      else toast.success("SMS & WhatsApp confirmation sent");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <Loader />;
  const tInvoices = invoices.filter((i) => i.tenant_id === form.tenant_id && i.status !== "paid");
  const tLeases = leases.filter((l) => l.tenant_id === form.tenant_id);

  return (
    <div>
      <PageHeader title="Payments & Receipts" subtitle="Record payments and issue receipts" icon={Receipt}
        actions={canCreate && <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-payment-btn" className="gap-2"><Plus size={16} /> Record Payment</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={Receipt} title="No payments yet" message="Record a tenant payment to generate a receipt."
          action={canCreate && <Button onClick={() => setOpen(true)}><Plus size={16} className="mr-2" />Record Payment</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Receipt #</TableHead><TableHead>Tenant</TableHead><TableHead>Narration</TableHead>
                <TableHead>Method</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell className="font-mono text-sm">{p.receipt_number}</TableCell>
                    <TableCell className="text-sm">{p.tenant_name}<div className="text-xs text-muted-foreground">{p.property_name} {p.unit_no}</div></TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{String(p.narration).replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="capitalize text-sm">{p.method}</TableCell>
                    <TableCell className="text-sm">{fmtDate(p.date)}</TableCell>
                    <TableCell className="text-right font-mono font-medium">{money(p.amount, cur)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" title="Receipt PDF" onClick={() => openPdf(`/payments/${p.id}/receipt`)} data-testid={`rcp-pdf-${p.id}`}><FilePdf size={16} /></Button>
                        <Button size="icon" variant="ghost" title="Email" onClick={() => email(p.id)} data-testid={`rcp-email-${p.id}`}><EnvelopeSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" title="SMS & WhatsApp" onClick={() => notify(p.id)} data-testid={`rcp-notify-${p.id}`}><ChatCircle size={16} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Tenant" value={form.tenant_id} onChange={(v) => set("tenant_id", v)}
              options={tenants.map((t) => ({ value: t.id, label: t.full_name }))} required testId="payment-tenant" />
            <SelectField label="Against Invoice" value={form.invoice_id} onChange={(v) => set("invoice_id", v)}
              options={[{ value: "", label: "— None —" }, ...tInvoices.map((i) => ({ value: i.id, label: `${i.invoice_number} (${money(i.total - i.amount_paid, cur)})` }))]} />
            <SelectField label="Lease" value={form.lease_id} onChange={(v) => set("lease_id", v)}
              options={[{ value: "", label: "— None —" }, ...tLeases.map((l) => ({ value: l.id, label: `${l.property_name} ${l.unit_no}` }))]} />
            <TextField label="Amount" type="number" value={form.amount} onChange={(v) => set("amount", v)} required testId="payment-amount" />
            <SelectField label="Method" value={form.method} onChange={(v) => set("method", v)} options={["mpesa", "bank", "cash", "cheque"]} />
            <SelectField label="Narration" value={form.narration} onChange={(v) => set("narration", v)} options={NARRATIONS} testId="payment-narration" />
            <TextField label="Reference" value={form.reference} onChange={(v) => set("reference", v)} />
            <DateField label="Date" value={form.date} onChange={(v) => set("date", v)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-payment-btn">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
